import { mapAttractionCategory } from "./mapAttractionCategory.js";

/**
 * @typedef {"search"|"upsell"|"order"} AttractionSource
 */

/**
 * @typedef {{
 *   value: number | null,
 *   currency: string | null,
 *   type: "paid"|"included"|"gift"|"unknown"
 * }} NormalizedPrice
 */

/**
 * @typedef {{
 *   isSelected: boolean,
 *   isAutoAttached: boolean
 * }} NormalizedState
 */

/**
 * @typedef {{
 *   id: string | number | null,
 *   name: string,
 *   descriptionShort: string,
 *   category: string,
 *   durationMinutes: number | null,
 *   price: NormalizedPrice,
 *   state: NormalizedState,
 *   source: AttractionSource,
 *   raw: any
 * }} NormalizedAttraction
 */

/**
 * @param {any} raw
 * @param {AttractionSource} [source]
 * @returns {NormalizedAttraction}
 */
export function normalizeAttraction(raw, source = "search") {
  const empty = () => ({
    id: null,
    name: "",
    descriptionShort: "",
    category: "",
    durationMinutes: null,
    price: { value: null, currency: null, type: "unknown" },
    state: { isSelected: false, isAutoAttached: false },
    source,
    raw,
  });

  if (raw == null || typeof raw !== "object") {
    return empty();
  }

  const id =
    raw.attraction_id != null && raw.attraction_id !== ""
      ? raw.attraction_id
      : raw.id != null && raw.id !== ""
        ? raw.id
        : null;

  const name = String(
    raw.name || raw.title || raw.display_text || raw.displayText || raw.badge_text || ""
  ).trim();

  const descriptionShort = String(
    raw.description_short || raw.descriptionShort || raw.description || ""
  ).trim();

  const dmRaw = raw.category ?? raw.category_name ?? raw.categoryName;
  const category = mapAttractionCategory(dmRaw);

  let durationMinutes = null;
  const d1 = raw.duration_minutes ?? raw.durationMinutes;
  const d2 = raw.duration;
  if (d1 != null && d1 !== "") {
    const n = Number(d1);
    if (Number.isFinite(n) && n > 0) durationMinutes = Math.round(n);
  } else if (d2 != null && d2 !== "") {
    const n = Number(d2);
    if (Number.isFinite(n) && n > 0) durationMinutes = Math.round(n);
  }

  const auto =
    raw.auto_attach === true ||
    raw.auto_attach === "true" ||
    raw.autoAttach === true ||
    raw.is_auto_attached === true;
  const selected =
    raw.is_selected === true ||
    raw.selected === true ||
    raw.isSelected === true;

  const state = {
    isSelected: Boolean(selected),
    isAutoAttached: Boolean(auto),
  };

  const price = resolveNormalizedPrice(raw, source);

  return {
    id,
    name,
    descriptionShort,
    category,
    durationMinutes,
    price,
    state,
    source,
    raw,
  };
}

/**
 * @param {Record<string, unknown>} raw
 * @param {AttractionSource} source
 * @returns {NormalizedPrice}
 */
function resolveNormalizedPrice(raw, source) {
  const rs =
    raw.rule_snapshot && typeof raw.rule_snapshot === "object"
      ? raw.rule_snapshot
      : raw.ruleSnapshot && typeof raw.ruleSnapshot === "object"
        ? raw.ruleSnapshot
        : null;

  const displayMode = String(
    (rs && rs.display_mode) || raw.display_mode || raw.displayMode || ""
  ).toLowerCase();
  const pricingMode = String(
    (rs && rs.pricing_mode) || raw.pricing_mode || raw.pricingMode || ""
  ).toLowerCase();

  if (displayMode === "gift") {
    return { value: null, currency: null, type: "gift" };
  }
  if (displayMode === "included") {
    return { value: null, currency: null, type: "included" };
  }
  if (pricingMode === "hidden_in_package") {
    return { value: null, currency: null, type: "included" };
  }

  const totalRaw =
    raw.total_gross != null
      ? Number(raw.total_gross)
      : raw.totalGross != null
        ? Number(raw.totalGross)
        : NaN;
  if (Number.isFinite(totalRaw) && totalRaw > 0) {
    const cur = raw.currency != null ? String(raw.currency) : "ILS";
    return { value: totalRaw, currency: cur, type: "paid" };
  }

  const priceObj = raw.price && typeof raw.price === "object" ? raw.price : null;
  if (priceObj) {
    const v =
      priceObj.value != null
        ? Number(priceObj.value)
        : priceObj.total_gross != null
          ? Number(priceObj.total_gross)
          : NaN;
    const t = String(priceObj.type || "").toLowerCase();
    if (t === "included") return { value: null, currency: null, type: "included" };
    if (t === "gift") return { value: null, currency: null, type: "gift" };
    if (Number.isFinite(v) && v >= 0) {
      return {
        value: v,
        currency: priceObj.currency != null ? String(priceObj.currency) : "ILS",
        type: "paid",
      };
    }
  }

  if (source === "search") {
    return { value: null, currency: null, type: "unknown" };
  }

  if (source === "upsell") {
    const base =
      raw.base_price != null
        ? Number(raw.base_price)
        : raw.basePrice != null
          ? Number(raw.basePrice)
          : NaN;
    if (Number.isFinite(base) && base >= 0) {
      return {
        value: base,
        currency: raw.currency != null ? String(raw.currency) : "ILS",
        type: "paid",
      };
    }
    return { value: null, currency: null, type: "unknown" };
  }

  // order
  if (displayMode === "gift" || pricingMode === "gift") {
    return { value: null, currency: null, type: "gift" };
  }

  return { value: null, currency: null, type: "unknown" };
}
